import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiArrowRight, 
  FiAward, 
  FiBookOpen, 
  FiUsers, 
  FiTarget,
  FiCheckCircle,
  FiStar,
  FiImage
} from 'react-icons/fi';
import { useContent } from '../context/ContentContext';
import api, { getImageUrl } from '../services/api';
import './HomePage.css';

const HomePage = () => {
  const { content } = useContent();
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await api.get('/upload/gallery', { params: { limit: 4 } });
        setGalleryImages(response.data.images || []);
      } catch (error) {
        console.error('Galeri yüklenemedi:', error);
      }
    };
    fetchGallery();
  }, []);

  // Varsayılan özellikler
  const defaultFeatures = [
    { icon: 'award', title: 'Deneyimli Kadro', desc: 'Alanında uzman 50+ öğretmen' },
    { icon: 'book', title: 'Modern Müfredat', desc: 'Güncel eğitim programları' },
    { icon: 'users', title: 'Küçük Sınıflar', desc: 'Max 15 kişilik sınıflar' },
    { icon: 'target', title: 'Başarı Odaklı', desc: '%95 başarı oranı' }
  ];

  // Varsayılan programlar
  const defaultPrograms = [
    { title: 'LGS Hazırlık', description: '8. sınıf öğrencileri için kapsamlı LGS hazırlık programı', color: '#667eea' },
    { title: 'YKS-TYT-AYT', description: 'Üniversite sınavına tam hazırlık', color: '#f093fb' },
    { title: 'Yabancı Dil', description: 'İngilizce ve Almanca dil kursları', color: '#4facfe' },
    { title: 'Kodlama', description: 'Yazılım ve robotik eğitimi', color: '#43e97b' }
  ];

  // Varsayılan yorumlar
  const defaultTestimonials = [
    { name: 'Ayşe Yılmaz', role: 'LGS Birincisi Velisi', text: 'Oğlum BMK sayesinde hayalindeki okula yerleşti. Öğretmenlerin ilgisi ve desteği inanılmazdı.', rating: 5 },
    { name: 'Mehmet Kaya', role: 'Tıp Fakültesi Öğrencisi', text: 'YKS hazırlığımda BMK\'nın katkısı çok büyük. Şu an hayalimdeki bölümde okuyorum.', rating: 5 },
    { name: 'Zeynep Demir', role: '9. Sınıf Öğrencisi', text: 'Küçük sınıflar ve birebir ilgi sayesinde derslerim çok iyileşti. Herkese tavsiye ederim.', rating: 5 }
  ];

  // Varsayılan neden biz listesi
  const defaultWhyUs = [
    { title: 'Deneyimli ve uzman öğretmen kadrosu', desc: '' },
    { title: 'Bireysel takip ve rehberlik hizmeti', desc: '' },
    { title: 'Modern ve teknolojik sınıflar', desc: '' },
    { title: 'Düzenli deneme sınavları ve analiz', desc: '' },
    { title: 'Veli bilgilendirme toplantıları', desc: '' }
  ];

  // İkon eşleştirme
  const iconMap = {
    award: FiAward,
    book: FiBookOpen,
    users: FiUsers,
    target: FiTarget,
    star: FiStar,
    check: FiCheckCircle
  };

  // Content'ten veya varsayılanlardan al
  const features = (content.features?.items || defaultFeatures).map(f => ({
    ...f,
    icon: iconMap[f.icon] || FiAward
  }));
  
  // programs veya items'dan al (eski veri uyumluluğu için)
  const programs = content.services?.programs || content.services?.items || defaultPrograms;
  const stats = content.stats?.items || [
    { number: '5000+', label: 'Mezun Öğrenci' },
    { number: '50+', label: 'Uzman Öğretmen' },
    { number: '15+', label: 'Yıllık Tecrübe' },
    { number: '%95', label: 'Başarı Oranı' }
  ];
  const testimonials = content.testimonials?.items || defaultTestimonials;
  const whyUsList = content.about?.whyUs || defaultWhyUs;
  const heroBadge = content.hero?.badge || '15+ Yıllık Eğitim Tecrübesi';
  const ctaTitle = content.cta?.title || 'Geleceğiniz İçin İlk Adımı Atın';
  const ctaDescription = content.cta?.description || 'Bursluluk sınavımıza başvurun, %100\'e varan burs fırsatlarından yararlanın!';

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-bg">
          <div className="hero-pattern"></div>
          <div className="hero-gradient"></div>
        </div>
        
        <div className="container hero-container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="hero-badge">
              <FiStar /> {heroBadge}
            </span>
            <h1 className="hero-title">
              {content.hero?.title || 'BMK Eğitim Kurumları'}
            </h1>
            <p className="hero-subtitle">
              {content.hero?.subtitle || 'Geleceğinizi Birlikte Şekillendiriyoruz'}
            </p>
            <p className="hero-desc">
              {content.hero?.description || 'Uzman kadromuz ve modern eğitim anlayışımızla öğrencilerimizi başarıya taşıyoruz.'}
            </p>
            <div className="hero-buttons">
              <Link to={content.hero?.buttonLink || '/bursluluk'} className="btn btn-primary btn-lg">
                {content.hero?.buttonText || 'Bursluluk Sınavına Başvur'}
                <FiArrowRight />
              </Link>
              <Link to={content.hero?.secondaryButtonLink || '/hakkimizda'} className="btn btn-outline btn-lg">
                {content.hero?.secondaryButtonText || 'Bizi Tanıyın'}
              </Link>
            </div>
          </motion.div>

          <motion.div 
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="hero-card hero-card-1">
              <FiAward className="hero-card-icon" />
              <span>{content.hero?.card1Text || '%95 Başarı'}</span>
            </div>
            <div className="hero-card hero-card-2">
              <FiUsers className="hero-card-icon" />
              <span>{content.hero?.card2Text || '5000+ Mezun'}</span>
            </div>
            <div className="hero-image-container">
              <div className="hero-image-placeholder">
                <img src="/logo.png" alt="BMK Logo" className="hero-logo" />
              </div>
            </div>
          </motion.div>
        </div>

        <div className="hero-wave">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="features section">
        <div className="container">
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="feature-icon">
                  <feature.icon />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="about-preview section">
        <div className="container">
          <div className="about-preview-grid">
            <motion.div 
              className="about-preview-content"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="section-title">Neden BMK Eğitim?</h2>
              <p className="about-preview-desc">
                {content.about?.description || 'BMK Eğitim Kurumları olarak 15 yılı aşkın tecrübemizle binlerce öğrencimizi hayallerindeki okullara yerleştirdik.'}
              </p>
              <ul className="about-preview-list">
                {whyUsList.map((item, index) => {
                  const title = typeof item === 'string' ? item : item.title;
                  const desc = typeof item === 'string' ? '' : item.desc;
                  return (
                    <li key={index}>
                      <FiCheckCircle /> 
                      <div>
                        <strong>{title}</strong>
                        {desc && <span>{desc}</span>}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <Link to="/hakkimizda" className="btn btn-secondary">
                Daha Fazla Bilgi <FiArrowRight />
              </Link>
            </motion.div>

            <motion.div 
              className="about-preview-image"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {content.about?.image ? (
                <img src={getImageUrl(content.about.image)} alt="Hakkımızda" className="about-image" />
              ) : (
                <div className="image-placeholder">
                  <FiBookOpen />
                  <span>Eğitim</span>
                </div>
              )}
              <div className="image-decoration"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="programs-section section section-dark">
        <div className="container">
          <div className="text-center">
            <motion.h2 
              className="section-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {content.services?.title || 'Eğitim Programlarımız'}
            </motion.h2>
            <motion.p 
              className="section-subtitle"
              style={{ color: 'rgba(255,255,255,0.8)', margin: '0 auto 3rem' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              {content.services?.subtitle || 'Her seviyeye uygun, kapsamlı eğitim programlarımızla başarıya ulaşın'}
            </motion.p>
          </div>

          <div className="programs-grid">
            {programs.slice(0, 4).map((program, index) => (
              <motion.div 
                key={index}
                className="program-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="program-icon" style={{ background: `linear-gradient(135deg, ${program.color} 0%, ${program.color}99 100%)` }}>
                  <FiBookOpen />
                </div>
                <h3>{program.title}</h3>
                <p>{program.description}</p>
                <Link to="/programlar" className="program-link">
                  Detaylar <FiArrowRight />
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center" style={{ marginTop: '3rem' }}>
            <Link to="/programlar" className="btn btn-outline btn-lg">
              Tüm Programları Gör <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="stat-item"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <span className="stat-number">{stat.number}</span>
                <span className="stat-label">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      {galleryImages.length > 0 && (
        <section className="home-gallery section">
          <div className="container">
            <div className="text-center">
              <motion.h2 
                className="section-title"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                Galerimiz
              </motion.h2>
              <motion.p 
                className="section-subtitle"
                style={{ margin: '0 auto 3rem' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                Kurumumuzdan kareler ve başarı hikayelerimiz
              </motion.p>
            </div>

            <div className="home-gallery-grid">
              {galleryImages.map((image, index) => (
                <motion.div 
                  key={image._id}
                  className="home-gallery-item"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className="home-gallery-image-wrapper">
                    {image.imageUrl ? (
                      <img src={getImageUrl(image.imageUrl)} alt={image.title} loading="lazy" />
                    ) : (
                      <div className="image-placeholder">
                        <FiImage />
                      </div>
                    )}
                    <div className="home-gallery-overlay">
                      <h3>{image.title}</h3>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center" style={{ marginTop: '3rem' }}>
              <Link to="/galeri" className="btn btn-outline btn-lg">
                Tüm Galeriye Git <FiArrowRight />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      <section className="testimonials-section section" style={{ background: 'var(--gray-50)' }}>
        <div className="container">
          <div className="text-center">
            <motion.h2 
              className="section-title"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Öğrenci ve Veli Yorumları
            </motion.h2>
            <motion.p 
              className="section-subtitle"
              style={{ margin: '0 auto 3rem' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              Başarıya ulaşan öğrencilerimizin deneyimleri
            </motion.p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="testimonial-rating">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FiStar key={i} />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <h4>{testimonial.name}</h4>
                    <span>{testimonial.role}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section section-dark">
        <div className="container">
          <motion.div 
            className="cta-content text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2>{ctaTitle}</h2>
            <p>{ctaDescription}</p>
            <div className="cta-buttons">
              <Link to="/bursluluk" className="btn btn-primary btn-lg">
                Hemen Başvur <FiArrowRight />
              </Link>
              <Link to="/iletisim" className="btn btn-outline btn-lg">
                Bize Ulaşın
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

